import { PROCEDURE_TYPE_LABELS } from '@/types/cgt'
import type { ReactElement } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { AlertCircle, Loader2 } from 'lucide-react'

/**
 * The loading and error states every report shows, in one place.
 *
 * Returns the element to render, or null when the query has data and the
 * caller should carry on. A report that invented its own spinner and its own
 * error wording would be the third dialect of "something went wrong" on a page
 * whose whole job is being read quickly.
 */
export function ReportStates<T>(query: UseQueryResult<T>): ReactElement | null {
  if (query.isPending) {
    return (
      <div className='flex items-center gap-2 px-4 py-6 text-xs text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Building the report…
      </div>
    )
  }
  if (query.isError) {
    return (
      <div className='flex items-start gap-2 px-4 py-6 text-xs text-destructive'>
        <AlertCircle className='h-4 w-4 shrink-0' />
        <span>
          <span className='font-medium'>This report could not be built.</span>{' '}
          {query.error instanceof Error ? query.error.message : 'Try a narrower window.'}
        </span>
      </div>
    )
  }
  return null
}

/** Axis label for a bucket: "8 Sep", "w/c 7 Sep", "Sep 2026". */
export function bucketLabel(iso: string, grain: 'daily' | 'weekly' | 'monthly'): string {
  const d = new Date(`${iso}T00:00:00Z`)
  if (grain === 'monthly') {
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' })
  }
  const day = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' })
  return grain === 'weekly' ? `w/c ${day}` : day
}

/** Shared recharts tooltip chrome, so three charts cannot drift apart. */
export const tooltipStyle = {
  fontSize: 12,
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
} as const

/**
 * Hand a built CSV to the browser.
 *
 * A blob URL rather than a data: URI — a few hundred KB of rows trips the URL
 * length limit in some browsers, and the failure is silent.
 */
export function downloadCsv(csv: string, fileName: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}

/**
 * A procedure type as a person reads it.
 *
 * Takes a plain string because reports 1.4 and 1.5 carry an `unknown` bucket
 * for a run whose template has no type — dropping those rather than labelling
 * them would quietly shrink the totals.
 */
export function typeLabel(type: string): string {
  return (PROCEDURE_TYPE_LABELS as Record<string, string>)[type] ?? type
}
